import axios, { AxiosResponse } from 'axios';
import { message } from 'antd';
import { BASE_URL } from 'constants/api';
import {
  SeasonContentListResponseInterface,
  SeasonContentAddRequestInterface,
  SeasonContentAddResponseInterface,
  SeasonContentRemoveRequestInterface,
  SeasonContentRemoveResponseInterface,
} from '../types/seasonContentTypes';

/**
 * Season Content Link API Service
 * Dedicated service for the new /api/v1/season/{id}/contents/ API
 *
 * Processing:
 * - Provides content query, add, replace, and remove features linked to Season
 * - Also manages AdditionalContentOriginLink for clip/trailer types
 */
class SeasonContentService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${BASE_URL}/api/v1/season`;
  }

  /**
   * Fetch all content list linked to Season
   * @param seasonId - Season ID
   * @param queryParams - Query parameters (optional)
   * @returns Content list linked to Season
   */
  public async getSeasonContents(
    seasonId: number,
    queryParams?: URLSearchParams
  ): Promise<SeasonContentListResponseInterface> {
    try {
      const url = queryParams
        ? `${this.baseUrl}/${seasonId}/contents/?${queryParams.toString()}`
        : `${this.baseUrl}/${seasonId}/contents/`;
      const response: AxiosResponse<SeasonContentListResponseInterface> = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch Season content list:', error);
      throw error;
    }
  }

  /**
   * Add content to Season
   * @param seasonId - Season ID
   * @param contentIds - Array of content IDs to add
   * @returns Addition result
   */
  public async addContentsToSeason(
    seasonId: number,
    contentIds: number[]
  ): Promise<SeasonContentAddResponseInterface> {
    try {
      const requestData: SeasonContentAddRequestInterface = { content_ids: contentIds };
      const response: AxiosResponse<SeasonContentAddResponseInterface> = await axios.post(
        `${this.baseUrl}/${seasonId}/contents/`,
        requestData
      );

      message.success(response.data.message);
      return response.data;
    } catch (error) {
      console.error('Failed to add content to Season:', error);
      throw error;
    }
  }

  /**
   * Unlink specific content from Season
   * @param seasonId - Season ID
   * @param contentIds - Array of content IDs to remove
   * @returns Removal result
   */
  public async removeContentsFromSeason(
    seasonId: number,
    contentIds: number[]
  ): Promise<SeasonContentRemoveResponseInterface> {
    try {
      const requestData: SeasonContentRemoveRequestInterface = { content_ids: contentIds };
      const response: AxiosResponse<SeasonContentRemoveResponseInterface> = await axios.delete(
        `${this.baseUrl}/${seasonId}/contents/`,
        { data: requestData }
      );

      message.success(response.data.message);
      return response.data;
    } catch (error) {
      console.error('Failed to remove content from Season:', error);
      throw error;
    }
  }
}

export const seasonContentService = new SeasonContentService();
